// SettingsModal.js — the Estimate Settings modal, lifted from the legacy
// TakeoffEstimateWorkspace so the Service Flavors view can tweak production
// rates / labor rates / modifiers / subcontractors / frequencies. Editing here
// re-derives the takeoff hours, so the scope panel + all flavors re-price live.
// Presentational + prop-driven (settings + onSettingsChange). Reuses te-* CSS.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AREA_CATEGORIES,
  AREA_TYPE_COLORS,
  AREA_TYPE_LABELS,
  DEFAULT_SERVICES_BY_TYPE,
  DEFAULT_PRODUCTION_RATES,
  RATE_UNIT_LABELS,
  SERVICES,
  SERVICE_LABELS,
} from '../../../SiteTakeoff/siteTakeoffConstants';
import { DEFAULT_ESTIMATE_SETTINGS } from '../estimateEngine';
import '../takeoffEstimateWorkspace.css';

const fmtCurrency = (value, maxDigits = 0) =>
  (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    maximumFractionDigits: maxDigits, minimumFractionDigits: maxDigits,
  });

const Button = ({ className = '', children, ...props }) => (
  <button type="button" className={`te-btn ${className}`} {...props}>{children}</button>
);

// Default production-rate clone for "reset to defaults".
const defaultProductionRates = () =>
  Object.entries(DEFAULT_ESTIMATE_SETTINGS.productionRates).reduce((acc, [key, value]) => ({
    ...acc, [key]: { ...value },
  }), {});

const ALL_SERVICES = Object.values(SERVICES);

const defaultAreaServices = () =>
  Object.keys(AREA_TYPE_LABELS).reduce((acc, type) => ({
    ...acc,
    [type]: [...(DEFAULT_SERVICES_BY_TYPE[type] || [])],
  }), {});

const mergeAreaServices = (saved = {}) =>
  Object.keys(AREA_TYPE_LABELS).reduce((acc, type) => ({
    ...acc,
    [type]: Array.isArray(saved[type])
      ? saved[type]
      : [...(DEFAULT_SERVICES_BY_TYPE[type] || [])],
  }), {});

const areaCategoryGroups = () => {
  const seen = new Set();
  const groups = Object.values(AREA_CATEGORIES).map((category) => {
    const types = (category.types || [])
      .filter((type) => AREA_TYPE_LABELS[type])
      .map((type) => ({ key: type, label: AREA_TYPE_LABELS[type] }));
    types.forEach(({ key }) => seen.add(key));
    return { label: category.label, types };
  }).filter((group) => group.types.length);

  const uncategorized = Object.entries(AREA_TYPE_LABELS)
    .filter(([type]) => !seen.has(type))
    .map(([key, label]) => ({ key, label }));
  if (uncategorized.length) groups.push({ label: 'Other', types: uncategorized });
  return groups;
};

// ── rates strip (summary + open-settings button) ─────────────────────────────

export function RatesStrip({ settings, services = [], onSettingsClick }) {
  const herbSub = services.find((s) => s.role === 'Herbicide' && s.delivery === 'subcontracted')?.sub;
  return (
    <>
      <div className="te-rates">
        <div className="rate"><div className="l">Maintenance</div><div className="v">{fmtCurrency(settings.laborRates.Maint, 2)}<small>/hr</small> <span className="def">default</span></div></div>
        <div className="rate"><div className="l">Arborist</div><div className="v">{fmtCurrency(settings.laborRates.Arborist, 2)}<small>/hr</small></div></div>
        <div className="rate"><div className="l">Herbicide (sub)</div><div className="v plum">{fmtCurrency(herbSub?.unitRate || settings.subcontractors.preEmergent.unitRate)}<small>/acre</small> <span className="def vendor">vendor</span></div></div>
        <div className="rate"><div className="l">Irrigation</div><div className="v">{fmtCurrency(settings.laborRates.Irrigation, 2)}<small>/hr</small> <span className="def">default</span></div></div>
      </div>
      <div className="te-settings-link">
        <span>Manage rates, production assumptions, modifiers and subcontractors in</span>
        <Button onClick={onSettingsClick}>Estimate settings</Button>
      </div>
    </>
  );
}

// ── panes ────────────────────────────────────────────────────────────────────

const ProductionPane = ({ settings, onSettingsChange }) => {
  const updateProductionRate = (serviceKey, field, value) => {
    const numeric = Number(value);
    onSettingsChange((current) => ({
      ...current,
      productionRates: {
        ...current.productionRates,
        [serviceKey]: {
          ...current.productionRates[serviceKey],
          [field]: Number.isFinite(numeric) ? numeric : 0,
        },
      },
    }));
  };

  const updateInference = (key, value) => {
    const numeric = Number(value);
    onSettingsChange((current) => ({
      ...current,
      irrigationZoneInference: {
        ...current.irrigationZoneInference,
        [key]: Number.isFinite(numeric) ? numeric : 0,
      },
    }));
  };

  const updateModifier = (key, value) => {
    const numeric = Number(value);
    onSettingsChange((current) => ({
      ...current,
      modifierTable: {
        ...current.modifierTable,
        [key]: Number.isFinite(numeric) ? numeric : 0,
      },
    }));
  };

  return (
    <>
      <div className="pane-hd">
        <div>
          <h3>Production rates</h3>
          <p>How fast a crew completes each service. Hours = quantity / rate x modifiers x coverage.</p>
        </div>
        <div className="right">
          <Button onClick={() => onSettingsChange((current) => ({
            ...current,
            productionRates: defaultProductionRates(),
          }))}
          >
            Reset to defaults
          </Button>
        </div>
      </div>
      <table className="te-rates-table">
        <thead><tr><th>Service</th><th>Rate</th><th>Quantity from</th><th>Frequency</th><th>Coverage</th></tr></thead>
        <tbody>
          {Object.entries(DEFAULT_PRODUCTION_RATES)
            // irrigation is costed by zone inference, not this SF/hr rate -> hide it
            .filter(([key]) => key !== 'irrigation_check')
            .map(([key, rateDefinition]) => {
              const rate = settings.productionRates[key] || rateDefinition;
              return (
                <tr key={key}>
                  <td className="name">{rateDefinition.label}</td>
                  <td>
                    <span className="num-in">
                      <input value={rate.rate} onChange={(event) => updateProductionRate(key, 'rate', event.target.value)} />
                      <span className="u">{RATE_UNIT_LABELS[rateDefinition.unit] || rateDefinition.unit}</span>
                    </span>
                  </td>
                  <td className="src">{
                    rateDefinition.unit === 'lf_hr' ? 'Mapped LF'
                      : rateDefinition.unit === 'trees_hr' ? 'Tree count'
                        : rateDefinition.unit === 'trees_or_sf_hr' ? 'Tree / shrub count'
                          : 'Mapped SF / counts'
                  }
                  </td>
                  <td>
                    <span className="num-in compact">
                      <input value={rate.visitsPerYear} onChange={(event) => updateProductionRate(key, 'visitsPerYear', event.target.value)} />
                      <span className="u">/yr</span>
                    </span>
                  </td>
                  <td>
                    <span className="num-in compact">
                      <input
                        value={Math.round((rate.coveragePerVisit || 0) * 100)}
                        onChange={(event) => updateProductionRate(key, 'coveragePerVisit', (Number(event.target.value) || 0) / 100)}
                      />
                      <span className="u">%</span>
                    </span>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      <div className="pane-hd secondary">
        <div>
          <h3>Irrigation zone inference</h3>
          <p>Divisors used to estimate zones from plant and turf counts.</p>
        </div>
      </div>
      <div className="mods">
        {Object.entries(settings.irrigationZoneInference).map(([key, value]) => (
          <div className="mod-row" key={key}>
            <span className="nm">{key.replace(/([A-Z])/g, ' $1')}</span>
            <span className="v"><input value={value} onChange={(event) => updateInference(key, event.target.value)} /></span>
          </div>
        ))}
      </div>
      <div className="pane-hd secondary">
        <div>
          <h3>Modifier adjustments</h3>
          <p>Multipliers applied to labor hours from area tags.</p>
        </div>
      </div>
      <div className="mods">
        {Object.entries(settings.modifierTable).map(([key, value]) => (
          <div className="mod-row" key={key}>
            <span className="nm">{key.replace(/-/g, ' ')}</span>
            <span className="v"><input value={value} onChange={(event) => updateModifier(key, event.target.value)} /><span className="x">x</span></span>
          </div>
        ))}
      </div>
    </>
  );
};

const AreaDefaultsPane = ({ url = '' }) => {
  const [defaults, setDefaults] = useState(() => defaultAreaServices());
  const [savedDefaults, setSavedDefaults] = useState(() => defaultAreaServices());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const grouped = useMemo(() => areaCategoryGroups(), []);
  const endpoint = `${url || ''}/api/customer/area-type-defaults`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setStatus('');
    axios.get(endpoint)
      .then((resp) => {
        if (cancelled) return;
        const merged = mergeAreaServices(resp.data?.defaults || {});
        setDefaults(merged);
        setSavedDefaults(merged);
        setDirty(false);
      })
      .catch(() => {
        if (cancelled) return;
        const systemDefaults = defaultAreaServices();
        setDefaults(systemDefaults);
        setSavedDefaults(systemDefaults);
        setDirty(false);
        setStatus('Using system defaults');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [endpoint]);

  const toggleService = (type, serviceCode) => {
    setDefaults((current) => {
      const services = current[type] || [];
      const next = services.includes(serviceCode)
        ? services.filter((service) => service !== serviceCode)
        : [...services, serviceCode];
      return { ...current, [type]: next };
    });
    setDirty(true);
    setStatus('');
  };

  const resetType = (type) => {
    setDefaults((current) => ({
      ...current,
      [type]: [...(savedDefaults[type] || [])],
    }));
    setDirty(true);
    setStatus('');
  };

  const resetAll = () => {
    setDefaults(mergeAreaServices(savedDefaults));
    setDirty(false);
    setStatus('Restored saved defaults');
  };

  const useSystemDefaults = () => {
    setDefaults(defaultAreaServices());
    setDirty(true);
    setStatus('');
  };

  const saveDefaults = async () => {
    setSaving(true);
    setStatus('');
    try {
      await axios.put(endpoint, { defaults });
      const merged = mergeAreaServices(defaults);
      setDefaults(merged);
      setSavedDefaults(merged);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('siteTakeoff:areaTypeDefaultsUpdated', {
          detail: { defaults: merged },
        }));
      }
      setDirty(false);
      setStatus('Defaults saved');
    } catch (err) {
      console.error('Failed to save area type defaults:', err);
      setStatus('Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="pane-hd">
        <div>
          <h3>Default services by area type</h3>
          <p>When a takeoff area is tagged with a type, these services are checked on automatically.</p>
        </div>
        <div className="right">
          {status && <span className="te-pane-status">{status}</span>}
          <Button onClick={resetAll}>Reset all</Button>
          <Button onClick={useSystemDefaults}>System defaults</Button>
          <Button className="primary" onClick={saveDefaults} disabled={!dirty || saving}>
            {saving ? 'Saving...' : 'Save defaults'}
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="te-inline-empty">Loading defaults...</div>
      ) : (
        <div className="area-types">
          {grouped.map((group) => (
            <div key={group.label} className="area-default-group">
              <div className="area-default-group-hd">{group.label}</div>
              {group.types.map(({ key: type, label }) => {
                const services = defaults[type] || [];
                return (
                  <div className="area-row" key={type}>
                    <div className="nm"><span className="swab" style={{ background: AREA_TYPE_COLORS[type] }} />{label}</div>
                    <div className="svc-chips">
                      {ALL_SERVICES.map((serviceCode) => {
                        const active = services.includes(serviceCode);
                        return (
                          <button
                            type="button"
                            key={serviceCode}
                            className={`svc-chip ${active ? 'on' : ''}`}
                            onClick={() => toggleService(type, serviceCode)}
                          >
                            {SERVICE_LABELS[serviceCode] || serviceCode}
                          </button>
                        );
                      })}
                    </div>
                    <button type="button" className="reset" onClick={() => resetType(type)}>Reset</button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Paved hardscape types that get pre-emergent on cracks & edges only. The
// Settings field below sets ONE fraction for all of them at once.
const PAVED_PRE_EMERGENT_TYPES = ['as_parking', 'hs_sidewalk', 'hs_plaza', 'pv_walkways', 'hs_dumpster_pad'];

const SubcontractorsPane = ({ settings, onSettingsChange }) => {
  const updatePreEmergent = (key, value) => {
    const numericFields = new Set(['unitRate', 'minPerVisit']);
    onSettingsChange((current) => ({
      ...current,
      subcontractors: {
        ...current.subcontractors,
        preEmergent: {
          ...current.subcontractors.preEmergent,
          [key]: numericFields.has(key) ? Number(value) || 0 : value,
        },
      },
    }));
  };

  // Current paved fraction, read off the first paved type (they move together).
  const coverageByType = settings.subcontractors?.preEmergent?.coverageByType || {};
  const pavedPct = Math.round(((coverageByType[PAVED_PRE_EMERGENT_TYPES[0]] ?? 0.05) * 100));
  // Write one percentage to every paved type's coverage fraction.
  const updatePavedCoverage = (value) => {
    const fraction = Math.min(1, Math.max(0, (Number(value) || 0) / 100));
    onSettingsChange((current) => {
      const cur = current.subcontractors.preEmergent.coverageByType || {};
      const next = { ...cur };
      PAVED_PRE_EMERGENT_TYPES.forEach((t) => { next[t] = fraction; });
      return {
        ...current,
        subcontractors: {
          ...current.subcontractors,
          preEmergent: { ...current.subcontractors.preEmergent, coverageByType: next },
        },
      };
    });
  };

  return (
    <>
      <div className="pane-hd">
        <div>
          <h3>Subcontractors</h3>
          <p>Services priced by unit instead of in-house labor hours.</p>
        </div>
        <div className="right"><Button className="primary">Add subcontractor</Button></div>
      </div>
      <div className="subs-list">
        <div className="sub-card">
          <div className="nm">Herbicide - Pre-emergent<small>{settings.subcontractors.preEmergent.vendorName} - priced per acre of pre-emergent area</small></div>
          <div className="col">
            <span className="l">Unit rate</span>
            <span className="num-in"><input value={settings.subcontractors.preEmergent.unitRate} onChange={(event) => updatePreEmergent('unitRate', event.target.value)} /><span className="u">$/ac</span></span>
          </div>
          <div className="col">
            <span className="l">Min per visit</span>
            <span className="num-in"><input value={settings.subcontractors.preEmergent.minPerVisit} onChange={(event) => updatePreEmergent('minPerVisit', event.target.value)} /><span className="u">$</span></span>
          </div>
          <div className="col"><span className="l">Quantity from</span><span className="v small">Pre-emergent SF / 43,560</span></div>
          <div className="col">
            <span className="l">Default frequency</span>
            <select value={settings.subcontractors.preEmergent.frequency} onChange={(event) => updatePreEmergent('frequency', event.target.value)}>
              {Object.entries(settings.frequencies).map(([key, freq]) => <option key={key} value={key}>{freq.label}</option>)}
            </select>
          </div>
          <div className="col">
            <span className="l">Paved crack/edge</span>
            <span className="num-in">
              <input
                aria-label="Paved crack/edge coverage percent"
                value={pavedPct}
                onChange={(event) => updatePavedCoverage(event.target.value)}
              />
              <span className="u">% of paved SF</span>
            </span>
          </div>
          <button type="button" className="ed">Edit</button>
        </div>
      </div>
    </>
  );
};

const LaborRatesPane = ({ settings, onSettingsChange }) => {
  const updateLaborRate = (role, value) => {
    onSettingsChange((current) => ({
      ...current,
      laborRates: { ...current.laborRates, [role]: Number(value) || 0 },
    }));
  };

  return (
    <>
      <div className="pane-hd"><div><h3>Labor rates</h3><p>Cost per crew hour by role.</p></div></div>
      <table className="te-rates-table">
        <thead><tr><th>Role</th><th>Cost / hr</th><th>Notes</th></tr></thead>
        <tbody>
          {Object.entries(settings.laborRates).map(([role, rate]) => (
            <tr key={role}>
              <td className="name">{role}</td>
              <td><span className="num-in"><input value={rate} onChange={(event) => updateLaborRate(role, event.target.value)} /><span className="u">$/hr</span></span></td>
              <td className="src">{role === 'Arborist' ? 'ISA-certified crew rate' : 'Company default loaded rate'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const FrequenciesPane = ({ settings, onSettingsChange }) => {
  const updateFrequency = (key, value) => {
    onSettingsChange((current) => ({
      ...current,
      frequencies: {
        ...current.frequencies,
        [key]: { ...current.frequencies[key], visitsPerYear: Number(value) || 0 },
      },
    }));
  };

  return (
    <>
      <div className="pane-hd"><div><h3>Visit frequencies</h3><p>The frequency presets that appear on the estimate screen.</p></div></div>
      <table className="te-rates-table">
        <thead><tr><th>Preset</th><th>Visits / yr</th><th>Used as default for</th></tr></thead>
        <tbody>
          {Object.entries(settings.frequencies).map(([key, freq]) => (
            <tr key={key}>
              <td className="name">{freq.label}</td>
              <td><span className="num-in compact"><input value={freq.visitsPerYear} onChange={(event) => updateFrequency(key, event.target.value)} /><span className="u">/yr</span></span></td>
              <td className="src">{key === 'weekly' ? 'Every-visit maintenance' : key === 'monthly' ? 'Irrigation check, trimming' : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── modal ────────────────────────────────────────────────────────────────────

export default function SettingsModal({ open, onClose, settings, onSettingsChange, url = '', initialTab = 'rates' }) {
  const [tab, setTab] = useState(initialTab || 'rates');
  useEffect(() => {
    if (open) setTab(initialTab || 'rates');
  }, [open, initialTab]);
  if (!open) return null;
  const panes = {
    rates: <ProductionPane settings={settings} onSettingsChange={onSettingsChange} />,
    areas: <AreaDefaultsPane url={url} />,
    subs: <SubcontractorsPane settings={settings} onSettingsChange={onSettingsChange} />,
    labor: <LaborRatesPane settings={settings} onSettingsChange={onSettingsChange} />,
    freq: <FrequenciesPane settings={settings} onSettingsChange={onSettingsChange} />,
  };
  return (
    <div className="te-modal-bg open" role="presentation" onClick={onClose}>
      <div className="te-modal" role="dialog" aria-modal="true" aria-label="Estimate settings" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div className="gear">⚙</div>
          <div>
            <h2>Estimate settings</h2>
            <div className="subtitle">Company-wide defaults - apply to every new estimate</div>
          </div>
          <button type="button" className="x" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">
          <div className="modal-tabs">
            {[
              ['rates', 'Production rates'],
              ['areas', 'Area type defaults'],
              ['subs', 'Subcontractors'],
              ['labor', 'Labor rates'],
              ['freq', 'Visit frequencies'],
            ].map(([key, label]) => (
              <button type="button" key={key} className={`modal-tab ${tab === key ? 'on' : ''}`} onClick={() => setTab(key)}>
                <span className="ic">{key === 'labor' ? '$' : '▤'}</span> {label}
              </button>
            ))}
          </div>
          <div className="modal-pane">{panes[tab]}</div>
        </div>
      </div>
    </div>
  );
}
